import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", body: "", cover_image: "", published: true };

function fmt(d) {
  try { return new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("journal");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [user, loading, nav]);

  const loadPosts = () => api.get("/journal?admin=true").then((r) => setPosts(r.data)).catch(() => {});
  const loadContacts = () => api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});

  useEffect(() => { if (user) { loadPosts(); loadContacts(); } }, [user]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (p) => { setEditing(p.id); setForm({ title: p.title, excerpt: p.excerpt, body: p.body, cover_image: p.cover_image || "", published: p.published }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/journal", form);
        toast.success("Entry created.");
      } else {
        await api.put(`/journal/${editing}`, form);
        toast.success("Entry updated.");
      }
      cancel();
      loadPosts();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/journal/${id}`);
    toast.success("Deleted.");
    loadPosts();
  };

  const markRead = async (c) => {
    await api.patch(`/contact/${c.id}/read`);
    loadContacts();
  };
  const removeContact = async (c) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/contact/${c.id}`);
    loadContacts();
  };

  if (loading || !user) return <div className="hm-container py-32 text-[color:var(--hm-ink-dim)]">Loading…</div>;

  return (
    <div data-testid="admin-dashboard">
      <section className="hm-container pt-32 pb-10 border-b border-[color:var(--hm-line)] flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="hm-eyebrow mb-2">Admin</p>
          <h1 className="font-display text-[28px]" style={{ fontWeight: 400 }}>Signed in as {user.email}</h1>
        </div>
        <button onClick={async () => { await logout(); nav("/admin/login"); }} className="hm-btn hm-btn-ghost" data-testid="admin-logout">Sign out</button>
      </section>

      <section className="hm-container py-8 flex gap-3">
        <button onClick={() => setTab("journal")} className={`px-4 py-2 text-sm uppercase tracking-[0.2em] border ${tab === "journal" ? "bg-[color:var(--hm-ink)] text-[#0a0a0c] border-[color:var(--hm-ink)]" : "border-[color:var(--hm-line)] text-[color:var(--hm-ink-dim)]"}`} data-testid="tab-journal">Journal</button>
        <button onClick={() => setTab("contacts")} className={`px-4 py-2 text-sm uppercase tracking-[0.2em] border ${tab === "contacts" ? "bg-[color:var(--hm-ink)] text-[#0a0a0c] border-[color:var(--hm-ink)]" : "border-[color:var(--hm-line)] text-[color:var(--hm-ink-dim)]"}`} data-testid="tab-contacts">
          Enquiries {contacts.filter(c => !c.read).length > 0 && <span className="ml-2 text-[color:var(--hm-pink)]">({contacts.filter(c => !c.read).length})</span>}
        </button>
      </section>

      {tab === "journal" && (
        <section className="hm-container pb-32">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-display text-[30px]" style={{ fontWeight: 400 }}>Journal entries</h2>
            <button onClick={startNew} className="hm-btn" data-testid="new-post-btn">+ New entry</button>
          </div>

          {editing && (
            <form onSubmit={save} className="border border-[color:var(--hm-line)] p-8 mb-10 space-y-4" data-testid="post-editor">
              <h3 className="font-display text-[24px] mb-2" style={{ fontWeight: 400 }}>{editing === "new" ? "New entry" : "Edit entry"}</h3>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="hm-input" placeholder="Title" required data-testid="post-title" />
              <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="hm-input" placeholder="Cover image URL (optional)" data-testid="post-cover" />
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="hm-input" rows={2} placeholder="Excerpt (1-2 sentences)" required data-testid="post-excerpt" />
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="hm-input" rows={12} placeholder="Body — separate paragraphs with a blank line" required data-testid="post-body" />
              <label className="flex items-center gap-2 text-[color:var(--hm-ink-dim)]">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="post-published" />
                <span>Published</span>
              </label>
              <div className="flex gap-3">
                <button type="submit" className="hm-btn" data-testid="post-save">Save entry</button>
                <button type="button" onClick={cancel} className="hm-btn hm-btn-ghost">Cancel</button>
              </div>
            </form>
          )}

          <div className="space-y-3">
            {posts.length === 0 && <p className="text-[color:var(--hm-ink-dim)]">No entries yet.</p>}
            {posts.map((p) => (
              <div key={p.id} className="border border-[color:var(--hm-line)] p-5 flex items-start justify-between gap-4" data-testid={`post-row-${p.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="hm-eyebrow">{fmt(p.created_at)}</span>
                    {!p.published && <span className="text-[10px] uppercase tracking-widest text-[color:var(--hm-pink)] border border-[color:var(--hm-pink)] px-2 py-0.5">Draft</span>}
                  </div>
                  <h4 className="font-display text-[20px]" style={{ fontWeight: 400 }}>{p.title}</h4>
                  <p className="text-[color:var(--hm-ink-dim)] text-sm mt-1 line-clamp-1">{p.excerpt}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => startEdit(p)} className="hm-btn hm-btn-ghost text-sm" data-testid={`edit-${p.id}`}>Edit</button>
                  <button onClick={() => remove(p.id)} className="text-sm text-[color:var(--hm-crimson-bright)] hover:underline" data-testid={`delete-${p.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "contacts" && (
        <section className="hm-container pb-32">
          <h2 className="font-display text-[30px] mb-8" style={{ fontWeight: 400 }}>Enquiries</h2>
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-[color:var(--hm-ink-dim)]">No enquiries yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className={`border p-5 ${c.read ? "border-[color:var(--hm-line)]" : "border-[color:var(--hm-pink)]"}`} data-testid={`contact-row-${c.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="hm-eyebrow">{fmt(c.created_at)}</span>
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[color:var(--hm-line)] text-[color:var(--hm-ink-dim)]">{c.enquiry_type}</span>
                      {!c.read && <span className="text-[10px] uppercase tracking-widest text-[color:var(--hm-pink)]">New</span>}
                    </div>
                    <h4 className="font-display text-[20px]" style={{ fontWeight: 400 }}>{c.name} <span className="text-[color:var(--hm-ink-dim)] text-[14px] font-sans">&lt;{c.email}&gt;</span></h4>
                    {c.organisation && <p className="text-[color:var(--hm-ink-dim)] text-sm">{c.organisation}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!c.read && <button onClick={() => markRead(c)} className="hm-btn hm-btn-ghost text-sm" data-testid={`read-${c.id}`}>Mark read</button>}
                    <button onClick={() => removeContact(c)} className="text-sm text-[color:var(--hm-crimson-bright)] hover:underline" data-testid={`delete-contact-${c.id}`}>Delete</button>
                  </div>
                </div>
                <p className="mt-4 text-[color:var(--hm-ink)] whitespace-pre-wrap leading-relaxed">{c.message}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
